import _isEmpty from 'lodash/isEmpty';

import { fetchJSON } from '@bonnet/next/fetch';

import { keyMappings } from '@atc/bonnet-parameters';
import { brands } from '@atc/brand';
import { cmApiUrl } from '@atc/cm-api-url';
import { createAction, createSelector, DuckSelector, objectDuckCreator } from '@atc/modular-redux';

import processCrawlPath from '@/utilities/processCrawlPath';

import { srpDuckPreset } from '@/ducks/presets/index.js';

import srpAdditionalCrawlPathsDuck from './srpAdditionalCrawlPathsDuck';
import srpCitiesCrawlPathsDuck from './srpCitiesCrawlPathsDuck';
import srpDealershipsCrawlPathsDuck from './srpDealershipsCrawlPathsDuck';
import srpListingTypesCrawlPathsDuck from './srpListingTypesCrawlPathsDuck';
import srpMakeCrawlPathsDuck from './srpMakeCrawlPathsDuck';
import srpModelCrawlPathsDuck from './srpModelCrawlPathsDuck';
import srpTrimCrawlPathsDuck from './srpTrimCrawlPathsDuck';
import srpYearCrawlPathsDuck from './srpYearCrawlPathsDuck';

const onlyContainsCertainSeoFilters = (query, seoFilterList, allowedKeys = []) => {
    const seoFiltersInQuery = Object.keys(query).filter((key) => seoFilterList.includes(key));

    return seoFiltersInQuery.length > 0 && seoFiltersInQuery.every((key) => allowedKeys.includes(key));
};

const srpContentDuck = objectDuckCreator({
    ...srpDuckPreset,
    store: 'content',
    initialState: {
        isIndexedZip: undefined,
        metaDescription: '',
        windowTitle: '',
        openGraphDescription: '',
        openGraphImage: '',
        openGraphSiteName: '',
        openGraphType: '',
        openGraphUrl: '',
        locationHeading: '',
        srpSelfServiceSeo: {},
        summaryContentData: {},
        makeSummaryData: {},
        editorials: [],
        sycUrl: '',
        queryId: '',
    },
}).extend({
    types: [
        'SET_CONTENT',
    ],
    creators: ({ types }) => ({
        clearCrawlPaths: () => async (dispatch) => {
            dispatch(srpAdditionalCrawlPathsDuck.creators.clear());
            dispatch(srpCitiesCrawlPathsDuck.creators.clear());
            dispatch(srpDealershipsCrawlPathsDuck.creators.clear());
            dispatch(srpListingTypesCrawlPathsDuck.creators.clear());
            dispatch(srpMakeCrawlPathsDuck.creators.clear());
            dispatch(srpModelCrawlPathsDuck.creators.clear());
            dispatch(srpTrimCrawlPathsDuck.creators.clear());
            dispatch(srpYearCrawlPathsDuck.creators.clear());
        },
        setContent: createAction(types.SET_CONTENT),
        fetchCrawlPaths: ({
            brand,
            channel,
            query,
            enableCrawlPath,
            enableIndexedLocations,
            enableTrimData,
            enabledCityDataWithZip,
            enableTopDealersData,
            enableDefaultQueryParams,
            isNational,
            ddpBasePath = '/car-dealers',
            queryParams = {},
        }) => async (dispatch, getState) => {

            const highIndexURLs = srpContentDuck.selectors.getSrpHighIndexURLs(getState()) || [];

            const EXCLUDED_SEO_FILTERS = ['vehicleUseType', 'vehicleStyleCodes', 'vehicleStyleCode', 'makeCodeList', 'makeCode', 'modelCodeList', 'modelCode',
                'seriesCodeList', 'seriesCode', 'trimCode', 'trimCodeList', 'minPrice', 'maxPrice', 'fuelTypeGroup', 'transmissionCodes', 'transmissionCode', 'engineCodes', 'engineCode',
                'driveGroup', 'moneyBackGuarantee', 'vehicleExchange', 'vehicleType', 'sellerType', 'extColorSimple', 'startYear', 'endYear'];

            const isKbbBranded = brand === brands.KBB_BRAND;
            const isFordBranded = brand === brands.FORD_BRAND;
            const isCadBranded = brand === brands.CAR_AND_DRIVER;

            if (query?.experience === 'commercial') {
                query.lifestyleCategory = 'Commercial';
            } else if (query?.experience === 'best-deals') {
                query.dealType = ['goodprice', 'greatprice'];
                query.excludedLifestyleCategory = 'Commercial';
            }

            const { makeCode, modelCode, trimCode, year, vehicleStyleCode, driveGroup, minPrice, maxPrice, fuelTypeGroup, listingType } = query;

            const hasMultipleSelections = (key) => Array.isArray(query[key]) && query[key].length > 1;
            const multipleCrawlParams = [
                'makeCode',
                'seriesCode',
                'modelCode',
                'seriesCode',
                'trimCode',
                'listingType',
                'vehicleStyleCode',
                'bodyStyleSubtypeCode',
                'featureCode',
                'listingFeature',
                'fuelTypeGroup',
                'mpgRange',
                'sellerType',
                'driveGroup',
                'engineCode',
                'extColorSimple',
            ];

            // Add more SEO parameters to keyMappings
            multipleCrawlParams.forEach((param) => {
                const isIncluded = keyMappings.some((mapping) => mapping.lsc === param);
                if (!isIncluded) {
                    keyMappings.push({ lsc: param, cs: '' });
                }
            });

            const requestCrawlPath = !keyMappings.filter((x) => multipleCrawlParams.includes(x.lsc))
                .find((x) => hasMultipleSelections(x.lsc) || hasMultipleSelections(x.cs));

            const isTrimUrl = !!trimCode && !year;
            const isYearUrl = !!year && !trimCode;
            const isYmmtUrl = !!(year && makeCode && modelCode && trimCode);
            const isAllCarsUrl = !makeCode && !modelCode && !trimCode;
            const isMakeUrl = !!(makeCode && !modelCode && !trimCode);
            const isModelUrl = !!(makeCode && modelCode && ((enableCrawlPath && enableTrimData) || !trimCode));
            const onlyContainsMakeOrMakeModel = onlyContainsCertainSeoFilters(query, EXCLUDED_SEO_FILTERS, ['makeCode', 'makeCodeList', 'modelCode', 'modelCodeList']);

            // todo: refactor disableNonCitiesCrawlpath and remove isKbbBranded check
            const disableNonCitiesCrawlpath = !isKbbBranded && !!(vehicleStyleCode && (year || makeCode || modelCode || trimCode));
            const disableTrimList = isTrimUrl || isYearUrl || isYmmtUrl || disableNonCitiesCrawlpath;

            const hasNoFilters = !Object.keys(query).some((key) => EXCLUDED_SEO_FILTERS.includes(key));

            // todo: Refacor shouldFetchTrimsData, shouldFetchYearsData, fbaShouldRenderCitiesList and remove isCadBranded check
            const shouldFetchTrimsData = ((enableCrawlPath && enableTrimData) || !disableTrimList);
            const shouldFetchYearsData = (isKbbBranded || (isModelUrl && !(isYearUrl || disableNonCitiesCrawlpath)));
            const shouldFetchTopDealersData = enableTopDealersData && (listingType === 'NEW' || listingType === 'USED') && hasNoFilters;
            const fbaShouldRenderCitiesList = !isCadBranded && !!(!makeCode && !vehicleStyleCode && !driveGroup && !minPrice && !maxPrice && !fuelTypeGroup && !year);
            const shouldFetchCityCrawlpath = (enableCrawlPath && enabledCityDataWithZip) || fbaShouldRenderCitiesList;

            const shouldFetchCrawlpaths = () => {
                if (isKbbBranded && (listingType === 'NEW' || listingType === 'CERTIFIED')) {
                    return false;
                }

                if (isCadBranded && listingType !== 'NEW' && listingType !== 'USED') {
                    return false;
                }
                return requestCrawlPath;
            };

            const crawlPathsRequestOptions = {
                circuitBreaker: {
                    id: 'lsc_crawlpath',
                    timeout: 3000,
                    resetTimeout: 300000,
                    fallback: {},
                },
                query: {
                    ...query,
                    facetMinCount: 3,
                },
                headers: {
                    'X-fwd-svc': 'atc',
                    'X-CoxAuto-Caller-Id': 'CI2355308',
                },
            };

            const nationalQuery = {
                ...crawlPathsRequestOptions?.query,
            };
            if (isNational) {
                delete nationalQuery.city;
                delete nationalQuery.state;
                delete nationalQuery.zip;
                delete nationalQuery.location;
                delete nationalQuery.searchRadius;
            }

            const nationalCrawlPathOptions = {
                ...crawlPathsRequestOptions,
                query: nationalQuery,
            };

            const fetches = {};

            if (shouldFetchCrawlpaths()) {
                if (enableDefaultQueryParams) {
                    Object.assign(crawlPathsRequestOptions.query, queryParams);
                }
                if (channel === 'ATC,PREVIEW') {
                    crawlPathsRequestOptions.query.channel = 'ATC';
                } else {
                    crawlPathsRequestOptions.query.channel = channel;
                }

                if (!disableNonCitiesCrawlpath && isAllCarsUrl) {
                    fetches.makesCrawlPaths = fetchJSON(cmApiUrl('/rest/lsc/crawl/makes'), crawlPathsRequestOptions);
                }

                if (shouldFetchCityCrawlpath) {
                    const citiesOptions = isNational ? nationalCrawlPathOptions : crawlPathsRequestOptions;

                    // Remove minPrice and maxPrice from cities query
                    const citiesQuery = { ...citiesOptions.query };
                    delete citiesQuery.minPrice;
                    delete citiesQuery.maxPrice;

                    const citiesOptionsWithoutPrice = {
                        ...citiesOptions,
                        query: citiesQuery,
                    };

                    fetches.citiesCrawlPaths = fetchJSON(cmApiUrl('/rest/lsc/crawl/cities'), citiesOptionsWithoutPrice);
                }

                if (!disableNonCitiesCrawlpath && isMakeUrl) {
                    fetches.modelCrawlPaths = fetchJSON(cmApiUrl('/rest/lsc/crawl/models'), crawlPathsRequestOptions);
                }

                if (!disableNonCitiesCrawlpath && isModelUrl) {
                    if (crawlPathsRequestOptions.query.modelCode && shouldFetchTrimsData) {
                        fetches.trimCrawlPaths = fetchJSON(cmApiUrl('/rest/lsc/crawl/trims'), crawlPathsRequestOptions);
                    }
                }

                if (makeCode) {
                    if (!isFordBranded && !disableNonCitiesCrawlpath) {
                        fetches.listingTypesCrawlPaths = fetchJSON(cmApiUrl('/rest/lsc/crawl/listingtypes'), crawlPathsRequestOptions);
                    }

                    if (shouldFetchYearsData) {
                        fetches.yearCrawlPaths = fetchJSON(cmApiUrl('/rest/lsc/crawl/modelyears'), crawlPathsRequestOptions);
                    }
                }

            }

            if (shouldFetchTopDealersData) {
                fetches.topDealersPaths = fetchJSON(cmApiUrl('/rest/lsc/crawl/topdealers'), crawlPathsRequestOptions);
            }

            if (shouldFetchCrawlpaths() || shouldFetchTopDealersData) {

                const fetchResults = await Promise.all(Object.values(fetches));
                const pageData = fetchResults.reduce((acc, data, index) => {
                    const key = Object.keys(fetches)[index];
                    acc[key] = data;
                    return acc;
                }, {});

                if (pageData && Object.hasOwn(pageData, 'makesCrawlPaths')) {
                    dispatch(srpMakeCrawlPathsDuck.creators.setKeys({ makesData: pageData.makesCrawlPaths }));
                }

                if (pageData && Object.hasOwn(pageData, 'citiesCrawlPaths') && Array.isArray(pageData.citiesCrawlPaths.links) && !isNational) {
                    pageData.citiesCrawlPaths.links = pageData.citiesCrawlPaths.links.filter((crawlpathData) => crawlpathData?.zip !== query?.zip);
                }

                await processCrawlPath({
                    brand,
                    data: pageData,
                    dispatch,
                    enableIndexedLocations,
                    isIndexedZip: srpContentDuck.selectors.getIsIndexedZip(getState()),
                    srpSelfServiceSeo: srpContentDuck.selectors.getSrpSelfServiceSeo(getState()),
                    srpHighIndexURLs: highIndexURLs,
                    query,
                    ddpBasePath,
                    options: {
                        isNational,
                        onlyContainsMakeOrMakeModel,
                    },
                });
            }
        },
    }),
    reducer: (state, action, { types }) => {
        switch (action.type) {

            case types.SET_CONTENT: {
                const { pageData = {} } = action.payload;

                // hacky to get the custom content by tabbed content
                const [customContent] = pageData.editorialContent;

                return {
                    ...state,
                    metaDescription: pageData.metaDescription || pageData.defaultMetaDescription,
                    metaKeywords: pageData.metaKeywords || '',
                    metaRobots: pageData.metaRobots,
                    windowTitle: pageData.windowTitle || pageData.title,
                    // openGraph content for Facebook graph
                    openGraphDescription: pageData.openGraphDescription,
                    openGraphImage: pageData.openGraphImage,
                    openGraphSiteName: pageData.openGraphSiteName,
                    openGraphTitle: pageData.windowTitle || pageData.title,
                    openGraphType: pageData.openGraphType,
                    openGraphUrl: pageData.openGraphUrl,
                    // selfService content data for meta tags
                    selfServiceWindowTitle: pageData.selfServiceWindowTitle,
                    selfServiceMetaDescription: pageData.selfServiceMetaDescription,
                    selfServiceLocationHeading: pageData.selfServiceLocationHeading,

                    locationHeading: pageData.locationHeading,
                    summaryContentData: pageData.summaryContentData,
                    makeSummaryData: pageData.makeSummaryData,
                    editorials: pageData.editorialContent,
                    sycUrl: pageData.sycUrl,
                    queryId: pageData.queryId,
                    customContent,
                };
            }

            default:
                return state;
        }
    },
    selectors: () => ({
        getEditorials: new DuckSelector((selectors) => createSelector(
            selectors.getDuckState,
            ({ editorials }) => editorials,
        )),
        getMakeSummaryData: new DuckSelector((selectors) => createSelector(
            selectors.getDuckState,
            ({ makeSummaryData }) => makeSummaryData,
        )),
        getLocationHeading: new DuckSelector((selectors) => createSelector(
            selectors.getDuckState,
            ({ locationHeading }) => locationHeading,
        )),
        getIsIndexedZip: new DuckSelector((selectors) => (state) => selectors.getDuckState(state).isIndexedZip),
        getQueryId: new DuckSelector((selectors) => (state) => selectors.getDuckState(state).queryId),
        getSrpSelfServiceSeo: new DuckSelector((selectors) => (state) => selectors.getDuckState(state).srpSelfServiceSeo),
        getSrpHighIndexURLs: new DuckSelector((selectors) => (state) => selectors.getDuckState(state).srpHighIndexURLs),
        getCustomContent: new DuckSelector((selectors) => (state) => selectors.getDuckState(state).customContent),
        isEnableCustomContent: new DuckSelector((selectors) => (state) => {
            const customContent = selectors.getCustomContent(state);

            const isHasHeader = !_isEmpty(customContent?.header);
            const isHasBody = !_isEmpty(customContent?.body);

            return isHasHeader && isHasBody;
        }),
    }),
});

export default srpContentDuck;
